// Referrals in Account (decision 55, docs/referrals-spec.md section 3): the pure helpers the section and the tests
// share (no React, no Supabase, so tests/e2e/referrals.spec.js imports them as they are). The section's 2 reads,
// in Referrals.jsx, are the database's (migration 79, docs/data-model.md 10.7), each answering for the acting
// person, so while staff view as a member they're that member's:
// - hub.my_referral_offers(): 1 row per product ticked for them, with its link's parts, its fee and its
//   wording, and their primary code. Nothing for a product they aren't ticked for.
// - hub.my_introductions(): their own lines a member may see, newest first, by the referred person's name
//   only. Never an email, a code, a source or a flag: the function leaves them out.
// The link, the fee line and the money are the old site's (docs/live-fixes/members-site/index.html,
// buildRefLink, refPayLine and fmtGBP), byte for byte, so a link shared from the old page still works.
import { uk } from '../lib/format.js'

const MONTHS3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// The member's link for 1 offer, exactly as the old site's buildRefLink made it: the offer's link, then ?
// or &, the offer's parameter (ref for Tally, client_reference_id for Stripe) and the code. '' when the
// offer has no link yet or the member has no code.
export function buildRefLink(offer, code) {
  if (!offer || !offer.intake_link || !code) return ''
  return offer.intake_link + (offer.intake_link.includes('?') ? '&' : '?') + (offer.ref_param || 'ref') + '=' + encodeURIComponent(code)
}

// £12.34, £23, £1,234.50: the old site's fmtGBP. Nothing (null) is £0, as its totals showed it.
export function gbp(v) {
  const n = v == null || Number.isNaN(Number(v)) ? 0 : Number(v)
  return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })
}

const hasFee = offer => offer && offer.fee_value != null && offer.fee_value !== '' && !Number.isNaN(Number(offer.fee_value))

// The fee line under a member's link: the old site's refPayLine, then a space and the offer's qualify rule
// when it has one. With no fee set, only the qualify rule shows.
export function payLine(offer) {
  if (!offer) return ''
  const rule = String(offer.qualify_rule || '').trim()
  let fee = ''
  if (hasFee(offer)) {
    if (offer.fee_kind === 'first_month') fee = `A successful referral pays you ${gbp(offer.fee_value)}, their first month.`
    else if (offer.fee_kind === 'percentage') fee = `A successful referral pays you ${Number(offer.fee_value)}% of what they pay.`
    else fee = `A successful referral pays you ${gbp(offer.fee_value)}.`
  }
  return [fee, rule].filter(Boolean).join(' ')
}

// The 3 boxes: Pending is how many are applied or joined; Owed is what's qualified and not paid yet;
// Paid is what's been paid
export function totals(lines = []) {
  const sum = status => lines.filter(l => l.status === status).reduce((s, l) => s + (Number(l.payout_amount) || 0), 0)
  return {
    pending: lines.filter(l => l.status === 'applied' || l.status === 'joined').length,
    owed: Math.round(sum('qualified') * 100) / 100,
    paid: Math.round(sum('paid') * 100) / 100,
  }
}

// 13 Oct 2026, in UK time
export function dayMonthYear(t) {
  if (!t) return ''
  const u = uk(t)
  return `${u.d} ${MONTHS3[u.m - 1]} ${u.y}`
}

// The section's state: quiet (nothing ticked and nothing to show) or full
export const isQuiet = ({ offers = [], lines = [] } = {}) => offers.length === 0 && lines.length === 0

// WhatsApp's share address for a link
export const whatsAppHref = link => `https://wa.me/?text=${encodeURIComponent(link)}`
